import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  QueryList,
  Type,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AnyMxSelectorCreatorMatching,
  MxSelector,
  MxSelectorCreator,
  MxSelectorCreatorNameMatchingWithReturnType,
  MxSelectorNameMatching
} from '@aviary/store/store.types';
import {MatSort, MatSortHeader, Sort} from '@angular/material/sort';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {RouterActions} from '@aviary/store/router/router.actions';
import {Action, createSelector, Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {MX_STANDARD_ANIMATION_TIMINGS, NO_OP, StatusChipColors} from '@aviary/store/common/common.types';
import {
  hasNoValue,
  isArray,
  isDefined,
  isObject,
  isPopulatedArray,
  isPopulatedString,
  isSelector,
  isString,
  isUndefined
} from '@aviary/store/common/typing.helpers';
import {DynamicComponentHostDirective} from '@aviary/directives/dynamic-component-host.directive';
import {
  ActionCreator,
  ActionIconButtonDefinition,
  ALWAYS,
  CellComponentProperties,
  CellCssClassDefinition,
  ChipColumnDefinition,
  ClickHandler,
  ColumnDefinition,
  ComponentColumnDefinition,
  ComponentExpansionColumnDefinition,
  ExpansionColumnDefinition,
  ExpansionTogglePosition,
  HiddenDataIndicatorRowDefinition,
  KebabActionDefinition,
  LinkRouteParamSelectedSelectorCreators,
  LinkRouteParamSelectorCreatorNames,
  LoadableSelectorContainer,
  NoArgumentActionCreator,
  NoArgumentClickHandler,
  RouteNameLinkColumnDefinition,
  RowCssClassDefinition,
  RowExpansionDefinition,
  RowSelectionTooltips,
  SelectorCreatorOrSelectorCreatorName,
  SelectorCreatorOrSelectorCreatorNameOrSimply,
  SimpleLinkColumnDefinition,
  StatusChipColumnDefinition,
  TableDefinition,
  TextColumnDefinition,
  TextExpansionColumnDefinition
} from '@aviary/components/table/table.component.types';
import {
  generateColumnHeaderFrom,
  generateColumnNameFrom,
  GENERATED_COLUMN_NAME_PREFIX,
  getSelectorCreatorFrom,
  resolveCellComponentProperties
} from '@aviary/components/table/table.component.helpers';
import {OneOrArrayOf} from '@aviary/store/utility-type.helpers';
import {Format, FormatPipe} from '@aviary/pipes/format.pipe';
import {RowSelectionService} from '@aviary/components/table/row-selection/row-selection.service';
import {EXPANDED_ROW_CLASS_NAME, RowExpansionService} from '@aviary/components/table/row-expansion/row-expansion.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {alwaysEmptyString, alwaysTrue} from '@aviary/store/common/common.selectors';
import {TableHeightAdjusterComponent} from '@aviary/components/table/table-height-adjuster/table-height-adjuster.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {ExpansionToggleComponent} from '@aviary/components/table/row-expansion/expansion-toggle.component';
import {AllRowsSelectionCheckboxComponent} from '@aviary/components/table/row-selection/all-rows-selection-checkbox/all-rows-selection-checkbox.component';
import {RowSelectionCheckboxComponent} from '@aviary/components/table/row-selection/row-selection-checkbox/row-selection-checkbox.component';
import {MatChip, MatChipSet} from '@angular/material/chips';
import {StatusChipComponent} from '@aviary/components/status-chip/status-chip.component';
import {BaseNamedRouterLinkDirective} from '@aviary/directives/base-named-router-link.directive';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {PaginationComponent} from '@aviary/components/table/pagination/pagination.component';
import {MatBadge} from '@angular/material/badge';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {TABLE_EXPANSION_TOGGLE_POSITION, TABLE_SHOW_ACTIONS_HEADER_TEXT} from '@aviary/injection-tokens/app-provided.tokens';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';

type InternalTextColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<TextColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'value' | 'whenPrinting'> &
  {
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    columnName: string;
  };

type InternalSimpleLinkColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<SimpleLinkColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'value' | 'whenPrinting'> &
  {
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    columnName: string;
    isLink: true;
    isSimpleLink: true;
    clickHandler: ClickHandler<ObjectType>,
  };

type InternalRouteNameLinkColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<RouteNameLinkColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'value' | 'whenPrinting'> &
  {
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    columnName: string;
    isLink: true;
    isRouteNameLink: true;
  };

type InternalChipColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<ChipColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'value' | 'chipColor' | 'whenPrinting'> &
  {
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    columnName: string;
    isChip: true;
    chipColorFor: (rowObject: ObjectType) => string;
  };

type InternalStatusChipColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<StatusChipColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'value' | 'whenPrinting'> &
  {
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    columnName: string;
    isChip: true;
    isStatusChip: true;
  };

type InternalComponentColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Omit<Required<ComponentColumnDefinition<ObjectType, SelectorsContainer>>, 'type' | 'whenPrinting'> &
  {
    isComponent: true;
  };

type InternalColumnDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Required<Omit<ColumnDefinition<ObjectType, SelectorsContainer>, 'selectorCreatorName' | 'actionCreator' | 'whenPrinting'>> &
  {
    type: ColumnDefinition<ObjectType, SelectorsContainer>['type'],
    columnName: string;
    valueSelectorCreator?: AnyMxSelectorCreatorMatching<ObjectType>;
    isComponent: boolean;
    componentClass?: Type<any>;  // eslint-disable-line @typescript-eslint/no-explicit-any -- Generic component typing is difficult, maybe impossible.
    isLink: boolean;
    clickHandler: ClickHandler<ObjectType>,
    isSimpleLink: boolean;
    isRouteNameLink: boolean;
    routeName: string;
    routeParams: LinkRouteParamSelectorCreatorNames<ObjectType, SelectorsContainer>;
    format: OneOrArrayOf<Format>;
    cssClasses: CellCssClassDefinition<ObjectType, SelectorsContainer>;
    truncate: boolean;
    isChip: boolean;
    chipColorFor: (rowObject: ObjectType) => string;
    isStatusChip: boolean;
    statusChipColors: StatusChipColors<string>;
    displayableStatusNames: Map<string, string>;
  };

type InternalActionIconButtonDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Required<Omit<
    ActionIconButtonDefinition<ObjectType, SelectorsContainer>,
    'clickActionCreator' | 'color' | 'tooltip' | 'badge' | 'superscript' | 'visible' | 'enabled'
  >> &
  {
    colorFor: MxSelectorCreator<[ObjectType], string>,
    tooltipFor: MxSelectorCreator<[ObjectType], string>,
    badgeFor: MxSelectorCreator<[ObjectType], string>,
    superscriptFor: MxSelectorCreator<[ObjectType], string>,
    visibleFor: MxSelectorCreator<[ObjectType], boolean>,
    enabledFor: MxSelectorCreator<[ObjectType], boolean>
  };

type InternalKebabActionDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Required<Omit<KebabActionDefinition<ObjectType, SelectorsContainer>, 'clickActionCreator' | 'visible'>> &
  {
    showMenuItemFor: (rowObject: ObjectType) => boolean
  };

type InternalExpansionCellDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  InternalTextExpansionCellDefinition<ObjectType, SelectorsContainer>
  | InternalComponentExpansionCellDefinition<ObjectType, SelectorsContainer>;

type InternalTextExpansionCellDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Required<Omit<TextExpansionColumnDefinition<ObjectType, SelectorsContainer>, 'type' | 'header' | 'value' | 'showIf'>> &
  {
    hasTextValue: true;
    hasComponentValue: false;
    headerFor: (rowObject: ObjectType) => string;
    headerCssClasses?: string;
    valueFor: (rowObject: ObjectType) => string;
    showIf: (rowObject: ObjectType) => boolean;
  };

type InternalComponentExpansionCellDefinition<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  Required<Omit<ComponentExpansionColumnDefinition<ObjectType, SelectorsContainer>, 'type' | 'header' | 'componentProperties' | 'showIf'>> &
  {
    hasTextValue: false;
    hasComponentValue: true;
    headerFor: (rowObject: ObjectType) => string;
    headerCssClasses?: string;
    componentProperties: CellComponentProperties<ObjectType, SelectorsContainer>;
    showIf: (rowObject: ObjectType) => boolean;
  };

interface InternalRowSelectionTooltips {
  whenUnselected: string;
  whenSelected: string;
  whenDisabled: string;
}

interface DynamicComponentDirectiveData<ObjectType, SelectorsContainer extends LoadableSelectorContainer> {
  row: ObjectType;
  column: InternalComponentColumnDefinition<ObjectType, SelectorsContainer>;
  componentClass: Type<any>;  // eslint-disable-line @typescript-eslint/no-explicit-any -- Generic component typing is difficult, maybe impossible.
  componentProperties: CellComponentProperties<ObjectType, SelectorsContainer>;
}

type DynamicComponentDirective<ObjectType, SelectorsContainer extends LoadableSelectorContainer> =
  DynamicComponentHostDirective<DynamicComponentDirectiveData<ObjectType, SelectorsContainer>>;

type DynamicComponentType = 'CELL' | 'EXPANSION_CELL' | 'EXPANSION_ROW';

const ICON_BUTTON_WIDTH_IN_REMS = 2.5; // For the default 40x40px icon button.
const ACTIONS_COLUMN_LABEL_WIDTH_IN_REMS = 4; // Only used if the column header is explicitly visible, and calculated action buttons width is smaller.
const MISSING_SORT_KEY_PREFIX = 'MISSING_SORT_KEY';

@Component({
  selector: 'mx-table',
  templateUrl: './table.component.html',
  animations: [
    trigger('expansionRowContentTransition', [
      state('collapsed', style({height: 0, 'padding-top': 0, 'padding-bottom': 0})),
      state('expanded', style({height: '*', 'padding-top': '*', 'padding-bottom': '*'})),
      transition('collapsed <=> expanded', animate(MX_STANDARD_ANIMATION_TIMINGS))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RowSelectionService, RowExpansionService],
  imports: [
    TableHeightAdjusterComponent, MatTable, MatSort, MatHeaderRow, MatRow, NgClass, MatFooterRow, SelectPipe, HiddenUntilLoadedDirective, MatColumnDef,
    MatCellDef, MatHeaderRowDef, MatRowDef, MatCell, DynamicComponentHostDirective, MatNoDataRow, MatHeaderCell, MatHeaderCellDef, ExpansionToggleComponent,
    AllRowsSelectionCheckboxComponent, RowSelectionCheckboxComponent, MatChipSet, FormatPipe, StatusChipComponent, MatChip, NgTemplateOutlet,
    BaseNamedRouterLinkDirective, MatFooterCell, MatFooterCellDef, MatTooltip, MatIcon, MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem,
    PaginationComponent, MatBadge, TruncateDirective, MatSortHeader, MatFooterRowDef, OutlinedButtonComponent, IconButtonComponent, TextButtonComponent
  ]
})
export class TableComponent<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer> implements AfterViewInit {

  @ViewChildren('cellComponentContainer', {read: DynamicComponentHostDirective}) dynamicCellComponentHostDirectiveQueryList:
    QueryList<DynamicComponentDirective<ObjectType, DefaultSelectorContainer>>;
  @ViewChildren('expansionCellComponentContainer', {read: DynamicComponentHostDirective}) dynamicExpansionCellComponentHostDirectiveQueryList:
    QueryList<DynamicComponentDirective<ObjectType, DefaultSelectorContainer>>;
  @ViewChildren('rowComponentContainer', {read: DynamicComponentHostDirective}) dynamicRowComponentHostDirectiveQueryList:
    QueryList<DynamicComponentDirective<ObjectType, DefaultSelectorContainer>>;

  @ViewChild('customNoDataComponentContainer', {read: DynamicComponentHostDirective}) dynamicNoDataComponentHostDirective: DynamicComponentHostDirective<never>;

  @Input() set tableDefinition(definition: TableDefinition<ObjectType, DefaultSelectorContainer>) {
    this.defaultSelectorContainer = definition.defaultSelectorContainer;

    this.tableName = String(definition.dataSource);

    this.dataSourceSelector =
      isString(definition.dataSource)
        ? this.defaultSelectorContainer[String(definition.dataSource)]
        : definition.dataSource;

    this.dataSource = this.store.select(this.dataSourceSelector);
    this.rowSelectionChangeAction = definition.rowSelectionChangeAction;
    this.rowSelectionEnabledFor = this.generateRowValueFunctionFor(definition.individualRowIsSelectable, true);
    this.rowSelectionTooltipsFor = this.generateRowSelectionTooltipsValueFunctionFor(definition.rowSelectionTooltips);

    this.rowSelectionDataSourceSelector = createSelector(
      this.dataSourceSelector,
      data => data?.filter(row => this.rowSelectionEnabledFor(row)) ?? []
    );

    this.rowCountSelector = createSelector(
      this.dataSourceSelector,
      data => data?.length ?? 0
    );

    this.actionButtons = this.generateActionButtonsFrom(definition.actionIconButtons);
    this.showActionButtons = isPopulatedArray(this.actionButtons);
    this.kebabMenuActions = this.generateKebabMenuActionsFrom(definition.kebabMenuActions);
    this.showKebabMenuActions = isPopulatedArray(this.kebabMenuActions);

    /*
     * Visibility can depend on store data outside the rows, since the visibleFor(row) selector can be based on any part of the overall NgRx state.
     * Therefore, making this selector depend on root state makes us recount visible buttons even when the dataSourceSelector's row data hasn't changed.
     * This might seem expensive -- recounting whenever the root state changes -- but each button's selector can reuse its memoized result.
     */
    this.maximumVisibleActionCountSelector = createSelector(
      this.dataSourceSelector,
      (state: object): object => state,
      (rows: ObjectType[], state: object): number => {
        if (hasNoValue(rows)) return 1;
        if (!isPopulatedArray(rows) || !isPopulatedArray(this.actionButtons)) return 0;

        return rows.reduce(
          (maximum: number, row: ObjectType): number => Math.max(maximum, this.countVisibleActionButtonsIn(row, state)),
          0
        );
      }
    );
    this.actionsColumnHeaderTextSelector = createSelector(
      this.maximumVisibleActionCountSelector,
      (count: number): string => `Action${count > 1 || this.showKebabMenuActions ? 's' : ''}`
    );
    this.rowCssClasses = definition.rowCssClasses ?? {};
    this.noDataComponentClass = definition.noDataComponentClass;
    this.noDataRowMessageText = definition.noDataRowMessage ?? 'No items to show.'
    this.noDataRowMessage = createSelector(() => this.noDataRowMessageText);
    this.useStandardNoDataMessageRow = !this.noDataComponentClass;
    this.useCustomNoDataComponent = !!this.noDataComponentClass;
    this.showPagination = definition.includePaginationControls ?? true;
    this.showBottomBorder = definition.showBottomBorder ?? this.showPagination;
    this.paginationAction = definition.paginationAction;
    this.triggerViewChildrenChangesOnInit = definition.triggerViewChildrenChangesOnInit ?? false;
    this.alwaysHideHeaderRow = definition.hideHeaderRow ?? false;

    this.initializeRowExpansionFrom(definition.rowExpansion);

    if (isDefined(definition.entityId)) {
      const entityIdSelector: MxSelector<number> =
        isString(definition.entityId)
          ? this.defaultSelectorContainer[String(definition.entityId)]
          : definition.entityId;

      this.entityId = this.selectPipe.transform(entityIdSelector);
    }

    this.initializeHiddenDataIndicatorRowFrom(definition.hiddenDataIndicatorRow);

    this.calculateActionsColumnWidth();
    this.buildInternalColumnsFrom(definition);

    this.numberOfDataColumns = this.columns.length;
    this._columnOrder = [
      ...this.useRowExpansion && this.expansionTogglePosition === 'START' ? ['_expansionRowToggle'] : [],
      ...definition.enableRowSelection ? ['_rowSelectionCheckboxes'] : [],
      ...this.columns.map(column => column.columnName),
      ...this.showActionButtons || this.showKebabMenuActions ? ['_actions'] : [],
      ...this.useRowExpansion && this.expansionTogglePosition === 'END' ? ['_expansionRowToggle'] : [],
    ];
    this._columnOrderForNoData = this._columnOrder.filter(columnName => columnName !== '_rowSelectionCheckboxes');
    if (isDefined(definition.responsiveColumnsDefinition)) {
      this.responsiveHideHeaderRow = !!definition.responsiveColumnsDefinition.responsiveHideHeaderRow;
      this.omitActionsSm = !!definition.responsiveColumnsDefinition.omitActionsSm;
      this.verticalActionsSm = !!definition.responsiveColumnsDefinition.verticalActionsSm;
      this.responsiveColumnOrderLg = this._columnOrder.filter((columnName) =>
        columnName.startsWith('_') || definition.responsiveColumnsDefinition?.columnNamesLg.includes(columnName));
      this.responsiveColumnOrderMd = this._columnOrder.filter((columnName) =>
        columnName.startsWith('_') || definition.responsiveColumnsDefinition?.columnNamesMd.includes(columnName));
      this.responsiveColumnOrderSm = this._columnOrder.filter((columnName) =>
        (columnName.startsWith('_') && columnName !== '_actions') ||
        (columnName === '_actions' && !this.omitActionsSm) ||
        definition.responsiveColumnsDefinition?.columnNamesSm.includes(columnName)
      );
      this.noDataColumnOrderLg = this.responsiveColumnOrderLg.filter(columnName => columnName !== '_rowSelectionCheckboxes');
      this.noDataColumnOrderMd = this.responsiveColumnOrderMd.filter(columnName => columnName !== '_rowSelectionCheckboxes');
      this.noDataColumnOrderSm = this.responsiveColumnOrderSm.filter(columnName => columnName !== '_rowSelectionCheckboxes');
    } else {
      this.responsiveColumnOrderLg = this.responsiveColumnOrderMd = this.responsiveColumnOrderSm = this._columnOrder;
      this.noDataColumnOrderLg = this.noDataColumnOrderMd = this.noDataColumnOrderSm = this._columnOrderForNoData;
    }

    this.columnOrderSelector = createSelector(
      this.viewportSelectors.currentViewportSize,
      (viewportSize) => {
        switch (viewportSize) {
          case 'sm':
            return this.responsiveColumnOrderSm;
          case 'md':
            return this.responsiveColumnOrderMd;
          case 'lg':
            return this.responsiveColumnOrderLg;
          default:
            return this._columnOrder;
        }
      }
    );

    this.columnOrderForNoDataSelector = createSelector(
      this.viewportSelectors.currentViewportSize,
      (viewportSize) => {
        switch (viewportSize) {
          case 'sm':
            return this.noDataColumnOrderSm;
          case 'md':
            return this.noDataColumnOrderMd;
          case 'lg':
            return this.noDataColumnOrderLg;
          default:
            return this._columnOrderForNoData;
        }
      }
    );
  }

// These will be replaced with the appropriate Observables based on the tableDefinition input.  We give them initial values to improve the initial rendering.
  public dataSource: Observable<ObjectType[]> = of([]);
  public actionsColumnWidthInRemsSelector: MxSelector<number>;
  public defaultSelectorContainer: DefaultSelectorContainer;

  public tableName: string;
  public responsiveHideHeaderRow: boolean = false;
  public omitActionsSm: boolean = false;
  public verticalActionsSm: boolean = false;
  public columns: InternalColumnDefinition<ObjectType, DefaultSelectorContainer>[];
  public numberOfDataColumns: number = 0;
  public readonly viewportSelectors = ViewportSelectors;
  public actionButtons: InternalActionIconButtonDefinition<ObjectType, DefaultSelectorContainer>[];
  public showActionButtons: boolean;
  public kebabMenuActions: InternalKebabActionDefinition<ObjectType, DefaultSelectorContainer>[];
  public showKebabMenuActions: boolean;
  public rowCssClasses?: RowCssClassDefinition<ObjectType, DefaultSelectorContainer>;
  public rowSelectionEnabledFor: (row: ObjectType) => boolean;
  public rowSelectionTooltipsFor: (row: ObjectType) => InternalRowSelectionTooltips;
  public rowSelectionDataSourceSelector: MxSelector<ObjectType[]>;
  public rowCountSelector: MxSelector<number>;
  private maximumVisibleActionCountSelector: MxSelector<number>;
  public actionsColumnHeaderTextSelector: MxSelector<string>;
  public rowSelectionChangeAction?: ActionCreator<ObjectType[]>;
  public readonly paginationSelectors = PaginationSelectors;
  public useStandardNoDataMessageRow: boolean;
  public noDataRowMessage: MxSelector<string>;
  public useCustomNoDataComponent: boolean;
  public useColumnBasedExpansionFor: (row: ObjectType) => boolean = () => false;
  public useRowBasedExpansion: boolean = false;
  public useRowBasedExpansionFor: (row: ObjectType) => boolean = () => false;
  public readonly renderRowBasedExpansionCompanion =
    (_index: number, row: ObjectType): boolean => this.useRowBasedExpansion && this.useRowBasedExpansionFor(row);
  public readonly rowExpansionService: RowExpansionService<ObjectType> = inject(RowExpansionService);
  public showPagination: boolean;
  public showBottomBorder: boolean;
  public paginationAction?: ((page: number, pageSize: number) => Action);
  public useHiddenDataIndicatorRow: boolean = false;
  public hiddenDataIndicatorRowMessage: MxSelector<string>;
  public hiddenDataIndicatorRowButtonLabel: MxSelector<string>;
  public hiddenDataIndicatorRowClickHandler: NoArgumentClickHandler;
  public showHiddenDataIndicatorRow: MxSelector<boolean>;
  public showHiddenDataIndicatorActionButton: MxSelector<boolean>;
  public hiddenDataIndicatorColumnOrder: MxSelector<string[]>;
  public alwaysHideHeaderRow: boolean;
  private initiallyExpandedFor: (row: ObjectType) => boolean = () => false;

  public ngAfterViewInit(): void {
    this.dynamicCellComponentHostDirectiveQueryList.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((directives: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>[]) =>
        this.createDynamicComponentsFor(directives, 'CELL')
      );

    this.dynamicExpansionCellComponentHostDirectiveQueryList.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((directives: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>[]) =>
        this.createDynamicComponentsFor(directives, 'EXPANSION_CELL')
      );

    this.dynamicRowComponentHostDirectiveQueryList.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((directives: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>[]) =>
        this.createDynamicComponentsFor(directives, 'EXPANSION_ROW')
      );

    if (this.noDataComponentClass) {
      const viewContainer = this.dynamicNoDataComponentHostDirective.viewContainer;
      viewContainer.clear();
      viewContainer.createComponent<Component>(this.noDataComponentClass);
      this.changeDetector.detectChanges();
    }

    // NOTE: This flag is most likely unnecessary with the "awaitingAnyLoad" subscription below.  See the deprecation comment in table.component.types.ts.
    if (this.triggerViewChildrenChangesOnInit) {
      this.dynamicCellComponentHostDirectiveQueryList.notifyOnChanges();
      this.dynamicExpansionCellComponentHostDirectiveQueryList.notifyOnChanges();
      this.dynamicRowComponentHostDirectiveQueryList.notifyOnChanges();
    }

    /*
      Rebuild dynamic component cells whenever the data finishes loading (which could be multiple times).  This will trigger the "changes" subscriptions above,
      and should prevent timing issues where dynamic components could be created before the data they need is in the store.
    */
    this.store.select(this.defaultSelectorContainer.awaitingAnyLoad)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(awaitingAnyLoad => {
        if (!awaitingAnyLoad) {
          this.dynamicCellComponentHostDirectiveQueryList?.notifyOnChanges();
          this.dynamicExpansionCellComponentHostDirectiveQueryList?.notifyOnChanges();
          this.dynamicRowComponentHostDirectiveQueryList?.notifyOnChanges();
        }
      });
  }

  public buildLinkParamsFor(column: InternalColumnDefinition<ObjectType, DefaultSelectorContainer>, row: ObjectType): LinkRouteParamSelectedSelectorCreators {
    return Object.entries(column.routeParams).reduce(
      (accumulator, [paramName, paramValue]) => {
        const selectorCreator = this.getSelectorCreatorFor(paramValue);
        if (!isDefined(selectorCreator)) {
          return accumulator;
        }
        return {
          ...accumulator,
          [paramName]: this.selectPipe.transform(selectorCreator(row))
        }
      },
      {} as LinkRouteParamSelectedSelectorCreators
    );
  }

  public columnOrderSelector: MxSelector<string[]>;
  public columnOrderForNoDataSelector: MxSelector<string[]>;

  public generateRowCssClassesFor(row: ObjectType): string {
    const initialRowClasses: string = this.generateRowExpansionCssClassesFor(row);
    if (!this.rowCssClasses) return initialRowClasses;

    return Object.entries(this.rowCssClasses).reduce(
      (accumulator, [className, selectorCreatorName]) =>
        this.selectPipe.transform(this.defaultSelectorContainer[String(selectorCreatorName)](row))
          ? `${accumulator} ${className}`
          : accumulator,
      initialRowClasses
    );
  }

  public generateRowExpansionCssClassesFor(row: ObjectType): string {
    if (!this.useRowExpansion) return '';

    const useColumnBasedExpansion: boolean = this.useColumnBasedExpansionFor(row);
    const useRowBasedExpansion: boolean = this.useRowBasedExpansionFor(row);
    if (!useColumnBasedExpansion && !useRowBasedExpansion) return '';

    let classes: string = 'mx-expandable-row';
    if (useColumnBasedExpansion) classes = `${classes} mx-expandable-row-column-based`;
    if (useRowBasedExpansion) classes = `${classes} mx-expandable-row-row-based`;
    if (this.rowExpansionService.isExpanded(row)) classes = `${classes} ${EXPANDED_ROW_CLASS_NAME}`;
    // if hawk becomes responsive, find a way to preserve mx-expandable-row-next-row-expanded from the rowExpansionService
    // maybe a data attribute instead of classes?

    return classes;
  }

  public generateCellCssClassesFor(row: ObjectType, cellCssClasses: CellCssClassDefinition<ObjectType, DefaultSelectorContainer>): string {
    if (!cellCssClasses) return '';

    return Object.entries(cellCssClasses).reduce(
      (accumulator, [className, selectorCreatorOrSelectorCreatorName]) =>
        selectorCreatorOrSelectorCreatorName === ALWAYS || this.generateRowValueFunctionFor(selectorCreatorOrSelectorCreatorName, false)(row)
          ? `${accumulator} ${className}`
          : accumulator,
      ''
    );
  }

  public onMatSortChange(angularSortState: Sort): void {
    this.store.dispatch(RouterActions.updateSortParams(angularSortState, this.entityId));
  }

  public visibleKebabMenuActionsFor(row: ObjectType): InternalKebabActionDefinition<ObjectType, DefaultSelectorContainer>[] | undefined {
    const visibleActions: InternalKebabActionDefinition<ObjectType, DefaultSelectorContainer>[] =
      this.kebabMenuActions.filter(menuAction => menuAction.showMenuItemFor(row));

    // Specifically return undefined instead of empty array to help the template know whether to display the kebab menu at all.
    return visibleActions.length > 0 ? visibleActions : undefined;
  }

  public expansionCellDefinitionsFor(columnIndex: number): InternalExpansionCellDefinition<ObjectType, DefaultSelectorContainer>[] {
    return this.columnBasedExpansionCellDefinitions?.filter((_, index) => columnIndex === index % this.numberOfDataColumns) ?? [];
  }

  private _columnOrder: string[];
  private _columnOrderForNoData: string[];
  private responsiveColumnOrderSm: string[];
  private responsiveColumnOrderMd: string[];
  private responsiveColumnOrderLg: string[];
  private noDataColumnOrderSm: string[];
  private noDataColumnOrderMd: string[];
  private noDataColumnOrderLg: string[];

  private dataSourceSelector: MxSelector<ObjectType[]>;
  private store: Store = inject(Store);
  private selectPipe: SelectPipe = inject(SelectPipe);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private expansionTogglePosition: ExpansionTogglePosition = inject(TABLE_EXPANSION_TOGGLE_POSITION);
  public showActionsHeaderText: boolean = inject(TABLE_SHOW_ACTIONS_HEADER_TEXT);
  private noDataComponentClass?: Type<any>;  // eslint-disable-line @typescript-eslint/no-explicit-any -- Generic component typing is difficult.
  private rowBasedExpansionComponentClass?: Type<Component>;
  private columnBasedExpansionCellDefinitions: InternalExpansionCellDefinition<ObjectType, DefaultSelectorContainer>[] = [];
  private useRowExpansion: boolean = false;
  private entityId?: number;
  private noDataRowMessageText: string;
  private triggerViewChildrenChangesOnInit: boolean

  private getSelectorCreatorFor<T>(
    definitionPropertyValue?: string | MxSelectorCreatorNameMatchingWithReturnType<ObjectType, DefaultSelectorContainer, T> | MxSelectorCreator<[ObjectType], T>
  ): MxSelectorCreator<[ObjectType], T> | undefined {
    return getSelectorCreatorFrom(this.defaultSelectorContainer, definitionPropertyValue);
  }

  private generateRowValueFunctionFor<T>(
    definitionPropertyValue:
      string | MxSelectorCreatorNameMatchingWithReturnType<ObjectType, DefaultSelectorContainer, T> | MxSelectorCreator<[ObjectType], T> | undefined,
    fallbackValue: T
  ): (rowObject: ObjectType) => T {
    const selectorCreator = this.getSelectorCreatorFor<T>(definitionPropertyValue);

    return isDefined(selectorCreator)
      ? (rowObject: ObjectType) => this.selectPipe.transform(selectorCreator(rowObject), fallbackValue)
      : () => fallbackValue;
  }

  private generateRowSelectionTooltipsValueFunctionFor(
    rowSelectionTooltips?: RowSelectionTooltips<ObjectType, DefaultSelectorContainer>
  ): (rowObject: ObjectType) => InternalRowSelectionTooltips {
    const whenUnselected: SelectorCreatorOrSelectorCreatorNameOrSimply<string, ObjectType, DefaultSelectorContainer> =
      rowSelectionTooltips?.whenUnselected ?? '';
    const whenSelected: SelectorCreatorOrSelectorCreatorNameOrSimply<string, ObjectType, DefaultSelectorContainer> =
      rowSelectionTooltips?.whenSelected ?? '';
    const whenDisabled: SelectorCreatorOrSelectorCreatorNameOrSimply<string, ObjectType, DefaultSelectorContainer> =
      rowSelectionTooltips?.whenDisabled ?? '';

    return (rowObject: ObjectType) => ({
      whenUnselected: this.generateRowValueFunctionFor(whenUnselected, isString(whenUnselected) ? whenUnselected : '')(rowObject),
      whenSelected: this.generateRowValueFunctionFor(whenSelected, isString(whenSelected) ? whenSelected : '')(rowObject),
      whenDisabled: this.generateRowValueFunctionFor(whenDisabled, isString(whenDisabled) ? whenDisabled : '')(rowObject),
    });
  }

  private countVisibleActionButtonsIn(row: ObjectType, state: object): number {
    /*
      Any selector can be called directly with the full NgRx state as its argument.  We call the creator and then call the created selector.
      Since we expect this to be called for ANY state update, this extracts the selector's current value much faster than snapshotting.
    */
    return this.actionButtons.filter(button => button.visibleFor(row)(state)).length;
  }

  private calculateActionsColumnWidth(): void {
    this.actionsColumnWidthInRemsSelector = createSelector(
      this.maximumVisibleActionCountSelector,
      count => {
        const buttonsWidth: number = (count * ICON_BUTTON_WIDTH_IN_REMS) + (this.showKebabMenuActions ? ICON_BUTTON_WIDTH_IN_REMS : 0);

        if (this.showActionsHeaderText && buttonsWidth < ACTIONS_COLUMN_LABEL_WIDTH_IN_REMS) {
          // If the Actions header is visible, and the calculated buttons' width is smaller than the label width, return the label width.
          return ACTIONS_COLUMN_LABEL_WIDTH_IN_REMS;
        }

        return buttonsWidth;
      }
    );
  }

  private buildInternalColumnsFrom(tableDefinition: TableDefinition<ObjectType, DefaultSelectorContainer>): void {
    const FALLBACK_COLUMN: InternalColumnDefinition<ObjectType, DefaultSelectorContainer> = {
      type: 'text',
      valueSelectorCreator: undefined,
      columnName: '',
      header: '',
      headerCssClasses: '',
      headerToolTip: '',
      sortable: false,
      sortKey: '',
      isComponent: false,
      isChip: false,
      isStatusChip: false,
      isLink: false,
      isSimpleLink: false,
      isRouteNameLink: false,
      clickHandler: NO_OP,
      routeName: '',
      routeParams: {},
      format: 'NONE',
      cssClasses: {},
      truncate: false,
      chipColorFor: () => '',
      statusChipColors: {
        'status-positive': [],
        'status-neutral': [],
        'status-negative': [],
        'status-transitional': [],
        'status-inactive': []
      },
      displayableStatusNames: new Map()
    };

    this.columns = tableDefinition.columns.map((columnDefinition, columnIndex) => {
      const columnName: string = generateColumnNameFrom(columnDefinition, columnIndex);
      const header: string = generateColumnHeaderFrom(columnDefinition, columnName, columnIndex);
      const headerCssClasses: string = columnDefinition.headerCssClasses ?? FALLBACK_COLUMN.headerCssClasses;
      const headerToolTip: string = columnDefinition.headerToolTip ?? FALLBACK_COLUMN.headerToolTip;
      const sortable: boolean = columnDefinition.sortable ?? false;
      const sortKey: string = this.generateColumnSortKeyFrom(columnDefinition, columnName, columnIndex);

      if (columnDefinition.type === 'component') {
        const internalColumn: InternalComponentColumnDefinition<ObjectType, DefaultSelectorContainer> = {
          columnName,
          header,
          headerCssClasses,
          headerToolTip,
          sortable,
          sortKey,
          isComponent: true,
          componentClass: columnDefinition.componentClass,
          componentProperties: columnDefinition.componentProperties ?? {}
        };

        return {
          ...FALLBACK_COLUMN,
          ...internalColumn
        };
      }

      const valueSelectorCreator: AnyMxSelectorCreatorMatching<ObjectType> | undefined = this.getSelectorCreatorFor(columnDefinition.value);

      const format: OneOrArrayOf<Format> = columnDefinition.format ?? 'NONE';
      const cssClasses: CellCssClassDefinition<ObjectType, DefaultSelectorContainer> = columnDefinition.cssClasses ?? {};
      const truncate: boolean = columnDefinition.truncate ?? false;

      if (columnDefinition.type === 'text') {
        const internalColumn: InternalTextColumnDefinition<ObjectType, DefaultSelectorContainer> = {
          columnName,
          overrideColumnName: columnDefinition.overrideColumnName ?? '',
          header,
          headerCssClasses,
          headerToolTip,
          sortable,
          sortKey,
          valueSelectorCreator,
          format,
          cssClasses,
          truncate
        };

        return {
          ...FALLBACK_COLUMN,
          ...internalColumn,
        };
      }

      if (columnDefinition.type === 'routeNameLink') {
        const internalColumn: InternalRouteNameLinkColumnDefinition<ObjectType, DefaultSelectorContainer> = {
          columnName,
          header,
          headerCssClasses,
          headerToolTip,
          sortable,
          sortKey,
          valueSelectorCreator,
          format,
          cssClasses,
          truncate,
          isLink: true,
          isRouteNameLink: true,
          routeName: columnDefinition.routeName,
          routeParams: columnDefinition.routeParams ?? {}
        };

        return {
          ...FALLBACK_COLUMN,
          ...internalColumn
        };
      }

      if (columnDefinition.type === 'chip') {
        const internalColumn: InternalChipColumnDefinition<ObjectType, DefaultSelectorContainer> = {
          columnName,
          header,
          headerCssClasses,
          headerToolTip,
          sortable,
          sortKey,
          valueSelectorCreator,
          format,
          cssClasses,
          truncate,
          isChip: true,
          chipColorFor: this.generateRowValueFunctionFor(columnDefinition.chipColor, 'status-inactive')
        };

        return {
          ...FALLBACK_COLUMN,
          ...internalColumn
        };
      }

      if (columnDefinition.type === 'statusChip') {
        const internalColumn: InternalStatusChipColumnDefinition<ObjectType, DefaultSelectorContainer> = {
          columnName,
          overrideColumnName: columnDefinition.overrideColumnName ?? '',
          header,
          headerCssClasses,
          headerToolTip,
          sortable,
          sortKey,
          valueSelectorCreator,
          format,
          cssClasses,
          truncate,
          isChip: true,
          isStatusChip: true,
          statusChipColors: columnDefinition.statusChipColors,
          displayableStatusNames: columnDefinition.displayableStatusNames ?? new Map()
        };

        return {
          ...FALLBACK_COLUMN,
          ...internalColumn
        };
      }

      // At this point, columnDefinition.type is either 'simpleLink' or 'actionLink', both of which end up internally as simple links.
      const internalColumn: InternalSimpleLinkColumnDefinition<ObjectType, DefaultSelectorContainer> = {
        columnName,
        header,
        headerCssClasses,
        headerToolTip,
        sortable,
        sortKey,
        valueSelectorCreator,
        format,
        cssClasses,
        truncate,
        isLink: true,
        isSimpleLink: true,
        clickHandler: this.generateLinkClickHandlerFor(columnDefinition) ?? NO_OP
      };


      return {
        ...FALLBACK_COLUMN,
        ...internalColumn
      };
    });
  }

  private generateColumnSortKeyFrom(columnDefinition: ColumnDefinition<ObjectType, DefaultSelectorContainer>, columnName: string, columnIndex: number): string {
    if (columnDefinition.sortKey) return columnDefinition.sortKey;

    return columnName.startsWith(GENERATED_COLUMN_NAME_PREFIX)
      ? `${MISSING_SORT_KEY_PREFIX}_${columnIndex}`
      : columnName;
  }

  private generateLinkClickHandlerFor(columnDefinition: ColumnDefinition<ObjectType, DefaultSelectorContainer>): ClickHandler<ObjectType> | undefined {
    return this.generateClickHandlerFor(
      columnDefinition.type === 'actionLink' ? columnDefinition.actionCreator : undefined,
      columnDefinition.type === 'simpleLink' ? columnDefinition.clickHandler : undefined
    );
  }

  private generateClickHandlerFor<ObjectType>(
    clickActionCreator?: ActionCreator<ObjectType>, clickHandler?: ClickHandler<ObjectType>
  ): ClickHandler<ObjectType> | undefined {
    return clickActionCreator
      ? (rowObject: ObjectType) => this.store.dispatch(clickActionCreator(rowObject))
      : clickHandler;
  }

  private generateNoArgumentClickHandlerFor(
    clickActionCreator?: NoArgumentActionCreator, clickHandler?: NoArgumentClickHandler
  ): NoArgumentClickHandler | undefined {
    return clickActionCreator
      ? () => this.store.dispatch(clickActionCreator())
      : clickHandler;
  }

  private generateRowSelectorFunctionFor<T>(
    definitionPropertyValue: string | SelectorCreatorOrSelectorCreatorName<ObjectType, DefaultSelectorContainer, T> | undefined,
    fallbackValue: T
  ): (rowObject: ObjectType) => MxSelector<T> {
    const selectorCreator: MxSelectorCreator<[ObjectType], T> | undefined = this.getSelectorCreatorFor<T>(definitionPropertyValue);
    const fallbackSelector: MxSelector<T> = createSelector(() => fallbackValue);
    const selectors: WeakMap<object, MxSelector<T>> = new WeakMap(); // Cache selectors created by each selector creator so we don't constantly replace them.

    return row => {
      if (isUndefined(selectorCreator)) return fallbackSelector;
      if (!isObject(row) && !isArray(row)) return selectorCreator(row);

      let selector: MxSelector<T> | undefined = selectors.get(row);

      if (isUndefined(selector)) {
        selector = selectorCreator(row);
        selectors.set(row, selector);
      }

      return selector;
    };
  }

  private generateRowStringSelectorFunctionFor(
    value?: SelectorCreatorOrSelectorCreatorNameOrSimply<string, ObjectType, DefaultSelectorContainer>
  ): (rowObject: ObjectType) => MxSelector<string> {
    return this.generateRowSelectorFunctionFor(value, isString(value) ? value : '');
  }

  private generateActionButtonsFrom(
    actionButtons?: ActionIconButtonDefinition<ObjectType, DefaultSelectorContainer>[]
  ): InternalActionIconButtonDefinition<ObjectType, DefaultSelectorContainer>[] {
    if (isUndefined(actionButtons)) return [];

    return actionButtons.map(actionButtonDefinition => ({
      ...actionButtonDefinition,
      colorFor: this.generateRowSelectorFunctionFor(actionButtonDefinition.color, 'on-surface-variant'),
      clickHandler: this.generateClickHandlerFor(actionButtonDefinition.clickActionCreator, actionButtonDefinition.clickHandler) ?? NO_OP,
      cssClasses: actionButtonDefinition.cssClasses ?? {},
      tooltipFor: this.generateRowStringSelectorFunctionFor(actionButtonDefinition.tooltip),
      badgeFor: this.generateRowStringSelectorFunctionFor(actionButtonDefinition.badge),
      superscriptFor: this.generateRowStringSelectorFunctionFor(actionButtonDefinition.superscript),
      visibleFor: this.generateRowSelectorFunctionFor(actionButtonDefinition.visible, true),
      enabledFor: this.generateRowSelectorFunctionFor(actionButtonDefinition.enabled, true)
    }));
  }

  private generateKebabMenuActionsFrom(
    kebabMenuActions?: KebabActionDefinition<ObjectType, DefaultSelectorContainer>[]
  ): InternalKebabActionDefinition<ObjectType, DefaultSelectorContainer>[] {
    if (!kebabMenuActions) return [];

    return kebabMenuActions.map(kebabActionDefinition => {
      const selectorCreator = this.getSelectorCreatorFor<boolean>(kebabActionDefinition.visible);

      return ({
        ...kebabActionDefinition,
        showMenuItemFor:
          isDefined(selectorCreator)
            ? (rowObject: ObjectType) => this.selectPipe.transform(selectorCreator(rowObject), false)
            : () => true,
        clickHandler: this.generateClickHandlerFor(kebabActionDefinition.clickActionCreator, kebabActionDefinition.clickHandler) ?? NO_OP
      });
    });
  }

  private generateExpansionCellsFrom(
    expansionCells?: ExpansionColumnDefinition<ObjectType, DefaultSelectorContainer>[]
  ): InternalExpansionCellDefinition<ObjectType, DefaultSelectorContainer>[] {
    if (!expansionCells) return [];

    return expansionCells.map(expansionCellDefinition => {
      const headerFor: (rowObject: ObjectType) => string =
        this.generateRowValueFunctionFor(expansionCellDefinition.header, isString(expansionCellDefinition.header) ? expansionCellDefinition.header : '');

      return expansionCellDefinition.type === 'text'
        ? {
          hasTextValue: true,
          hasComponentValue: false,
          headerFor,
          headerCssClasses: expansionCellDefinition.headerCssClasses ?? '',
          valueFor:
            this.generateRowValueFunctionFor(expansionCellDefinition.value, isString(expansionCellDefinition.value) ? expansionCellDefinition.value : ''),
          format: expansionCellDefinition.format ?? 'NONE',
          showIf: this.generateRowValueFunctionFor(expansionCellDefinition.showIf, true),
        }
        : {
          hasTextValue: false,
          hasComponentValue: true,
          headerFor,
          headerCssClasses: expansionCellDefinition.headerCssClasses ?? '',
          componentClass: expansionCellDefinition.componentClass,
          componentProperties: expansionCellDefinition.componentProperties ?? {},
          showIf: this.generateRowValueFunctionFor(expansionCellDefinition.showIf, true),
        };
    });
  }

  private createDynamicComponentsFor(directives: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>[], type: DynamicComponentType) {
    directives.forEach((directive: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>) => this.createDynamicComponentFor(directive, type));

    this.changeDetector.detectChanges();
  }

  private createDynamicComponentFor(directive: DynamicComponentDirective<ObjectType, DefaultSelectorContainer>, type: DynamicComponentType) {
    const containerData: DynamicComponentDirectiveData<ObjectType, DefaultSelectorContainer> = directive.data;
    const viewContainer = directive.viewContainer;
    viewContainer.clear();

    switch (type) {
      case 'CELL': {
        const containerColumn = containerData.column;
        const componentRef: ComponentRef<Component> = viewContainer.createComponent<Component>(containerColumn.componentClass);

        this.assignPropertiesIn(componentRef, containerColumn.componentProperties, containerData.row);
        componentRef.location.nativeElement.classList.add('max-w-full'); // Ensure that dynamic components don't take more space than their containers.

        break;
      }

      case 'EXPANSION_CELL': {
        const componentRef: ComponentRef<Component> = viewContainer.createComponent<Component>(containerData.componentClass);

        this.assignPropertiesIn(componentRef, containerData.componentProperties, containerData.row);
        componentRef.location.nativeElement.classList.add('max-w-full'); // Ensure that dynamic components don't take more space than their containers.

        break;
      }

      case 'EXPANSION_ROW': {
        if (this.rowBasedExpansionComponentClass) {
          const componentRef: ComponentRef<Component> = viewContainer.createComponent<Component>(this.rowBasedExpansionComponentClass);

          componentRef.instance['row'] = containerData.row;
        }
      }
    }
  }

  private assignPropertiesIn(
    componentRef: ComponentRef<Component>, properties: CellComponentProperties<ObjectType, DefaultSelectorContainer>, rowObject: ObjectType
  ) {
    const resolvedProperties: Record<string, unknown> =
      resolveCellComponentProperties(properties, rowObject, this.defaultSelectorContainer, selector => this.selectPipe.transform(selector));

    Object.entries(resolvedProperties).forEach(([propertyName, value]) =>
      componentRef.instance[propertyName] = value
    );
  }

  private initializeRowExpansionFrom(definition?: RowExpansionDefinition<ObjectType, DefaultSelectorContainer>): void {
    this.useRowExpansion = false;
    this.useRowBasedExpansion = false;
    this.useColumnBasedExpansionFor = () => false;
    this.useRowBasedExpansionFor = () => false;
    this.rowBasedExpansionComponentClass = undefined;
    this.columnBasedExpansionCellDefinitions = [];

    if (isUndefined(definition)) return;

    this.useRowExpansion = true;
    this.initiallyExpandedFor = () => false;

    if (isDefined(definition.rowBased)) {
      this.useRowBasedExpansion = true;
      this.useRowBasedExpansionFor = this.generateRowValueFunctionFor(definition.rowBased.showIf, true);
      this.rowBasedExpansionComponentClass = definition.rowBased.componentClass;
      this.initiallyExpandedFor = this.generateRowValueFunctionFor(definition.rowBased.initiallyExpanded, false);
    }

    if (isDefined(definition.columnBased)) {
      this.useColumnBasedExpansionFor = this.generateRowValueFunctionFor(definition.columnBased.showIf, true);
      this.columnBasedExpansionCellDefinitions = this.generateExpansionCellsFrom(definition.columnBased.columns);
      this.initiallyExpandedFor = this.generateRowValueFunctionFor(definition.columnBased.initiallyExpanded, false);
    }

    this.rowExpansionService.initializeInitiallyExpandedFor(this.initiallyExpandedFor);
  }

  private initializeHiddenDataIndicatorRowFrom(definition?: HiddenDataIndicatorRowDefinition<DefaultSelectorContainer>): void {
    if (isUndefined(definition)) return;

    this.useHiddenDataIndicatorRow = true;
    this.showHiddenDataIndicatorRow = this.getSelectorFor(definition.showIf, false);
    this.showHiddenDataIndicatorActionButton = this.getSelectorFor(definition.showActionButtonIf ?? alwaysTrue, true);

    // We need *some* column order for the special footer row.  Pick the first standard column, avoiding expansion controls and selection checkboxes.
    this.hiddenDataIndicatorColumnOrder = createSelector(
      this.showHiddenDataIndicatorRow,
      show => show ? [this.columns[0].columnName] : []
    );

    if (isPopulatedString(definition.overriddenNoDataRowMessageWhenShowing)) {
      this.noDataRowMessage = createSelector(
        this.showHiddenDataIndicatorRow,
        show => show ? definition.overriddenNoDataRowMessageWhenShowing as string : this.noDataRowMessageText
      );
    }

    this.hiddenDataIndicatorRowMessage = this.getStringSelectorFor(definition.message);
    this.hiddenDataIndicatorRowButtonLabel = this.getStringSelectorFor(definition.actionButtonLabel ?? 'Show');
    this.hiddenDataIndicatorRowClickHandler = this.generateNoArgumentClickHandlerFor(definition.clickActionCreator, definition.clickHandler) ?? NO_OP;
  }

  private getSelectorFor<T>(stringOrSelector: string | MxSelectorNameMatching<T, DefaultSelectorContainer> | MxSelector<T>, fallbackValue: T): MxSelector<T> {
    if (isSelector<T>(stringOrSelector)) return stringOrSelector;

    if (isString(stringOrSelector)) {
      const selectorFromContainer: MxSelector<T> | undefined = this.getSelectorFromDefaultContainer<T>(stringOrSelector);

      if (isDefined(selectorFromContainer)) return selectorFromContainer;
    }

    return createSelector(() => fallbackValue);
  }

  private getStringSelectorFor(stringOrSelector: string | MxSelectorNameMatching<string, DefaultSelectorContainer> | MxSelector<string>): MxSelector<string> {
    if (isSelector<string>(stringOrSelector)) return stringOrSelector;

    if (isString(stringOrSelector)) {
      const selectorFromContainer: MxSelector<string> | undefined = this.getSelectorFromDefaultContainer(stringOrSelector);

      return isDefined(selectorFromContainer) ? selectorFromContainer : createSelector(() => stringOrSelector);
    }

    return alwaysEmptyString;
  }

  private getSelectorFromDefaultContainer<T>(potentialSelectorName: string): MxSelector<T> | undefined {
    const selector: unknown = (this.defaultSelectorContainer as Record<string, unknown>)[potentialSelectorName];

    return isSelector<T>(selector) ? selector : undefined;
  }
}
